<template id="legal-confirmation-modal">
  <ion-page>
    <ion-header>
      <ion-toolbar>
        <ion-title>{{ title }}</ion-title>
        <ion-buttons class="modal-confirmation-buttons" slot="end">
          <ion-button
            class="close-button"
            @click="closeModal()"
            fill="solid"
            shape="round"
            color="medium-light" >
            <ion-icon slot="icon-only" :icon="closeSharp"></ion-icon>
          </ion-button>

          <!-- Allow confirmation only when fully loaded -->
          <ExtendableChip
            v-if="!hideConfirm"
            class="confirm-button"
            color="success"
            :title="i18n.$t('legal.modal.confirm')"
            extendOnClick
            @extendedClick="confirmAndCloseModal()"
            :disabled="content == null || currentlyConfirming"
            extendToLeft
            button
            textIcon
            :collapseTimeout="5000"
            >
            <template v-slot:permanent>
              <ion-icon :icon="checkmarkSharp" :alt="i18n.$t('legal.modal.confirm')"></ion-icon>
            </template>
          </ExtendableChip>
        </ion-buttons>
      </ion-toolbar>
    </ion-header>
    <ion-content>
      <div class="version-indicator" v-if="loadedVersion">{{i18n.$t('legal.modal.version')}}: {{loadedVersion}}</div>
      <hr>
      <MarkdownContent :source="content"></MarkdownContent>
    </ion-content>
  </ion-page>
</template>

<script>

import { IonPage, IonHeader, IonToolbar, IonTitle, IonButtons, IonButton, IonIcon, IonContent, modalController } from '@ionic/vue';
import { defineComponent, computed, watch, ref } from 'vue';

import { useStore } from 'vuex';

import { useDayjs } from '@/utils/dayjs';

import { useI18n } from "@/utils/i18n";

import { apiErrorToast } from '@/utils/error';

import { checkmarkSharp, closeSharp } from 'ionicons/icons';

import ExtendableChip from '@/components/ExtendableChip.vue';

import MarkdownContent from '@/components/MarkdownContent.vue';

import _ from 'lodash';

const LegalConfirmationModal = defineComponent({
  name: 'LegalConfirmationModal',
  components: { IonPage, IonHeader, IonToolbar, IonTitle, IonButtons, IonButton, IonIcon, IonContent, ExtendableChip, MarkdownContent },
  props: {
    'type': String,
    'version': String, //If version is not set, loads the latest version
    'hideConfirm': {
      type: Boolean,
      default: false
    }
  },
  setup(props) {
    const i18n = useI18n();
    const store = useStore();

    const { getSimpleLocalDate } = useDayjs();

    const title = computed(() => {
      if (entry.value != null && entry.value.descriptor != null) {
        return entry.value.descriptor;
      } else {
        return i18n.$t('legal.modal.loading_title');
      }
    });

    const loadedVersion = computed(() => {
      if (entry.value != null && entry.value.version != null) {
        return getSimpleLocalDate.value(entry.value.version, 'YYYYMMDD');
      }
      return undefined;
    });

    const currentlyConfirming = ref(false);

    const content = computed(() => { //TODO How to handle links to other documents, maybe process them to open inside this modal somehow?
      if (entry.value != null && entry.value.text != null) {
        return entry.value.text;
      }
      return undefined;
    }) //TODO Add loading spinner

    const entry = ref(null);

    const closeModal = function(){
      modalController.dismiss();
    }

    const confirmAndCloseModal = function(){
      let confirmedVersion = _.get(entry.value, ['version']);
      if (confirmedVersion != null) {
        currentlyConfirming.value = true;
        store.dispatch('auth/updateLegalDocument', { type: props.type, version: confirmedVersion }).then(() => {
          modalController.dismiss({isConfirmed: true});
        }).catch((error) => {
          apiErrorToast(i18n, error, true).then(() => closeModal())
          throw error;
        });
      }
    }

    //Watch for language change and fetch it
    watch(i18n.locale, (locale) => {
      if (locale != null) {
        store.dispatch('auth/fetchLegalDocument', { type: props.type, locale: locale, version: props.version })
        .then((newEntry) => entry.value = newEntry)
        .catch((error) => {
          entry.value = null;
          apiErrorToast(i18n, error, true).then(() => closeModal());
        });
      }
    }, { immediate: true});

    return { i18n, title, content, currentlyConfirming, loadedVersion, closeModal, confirmAndCloseModal, checkmarkSharp, closeSharp };
  }
});

export async function openLegalConfirmationModal(component, type, hideConfirm = false, version = undefined){
  if (component != null && (type != null)) {
    const modal = await modalController
      .create({
        component,
        cssClass: 'legal-confirmation-modal',
        componentProps: {
          type,
          hideConfirm,
          version
        },
      })
    modal.present();
    return modal.onWillDismiss();
  }
}

export default LegalConfirmationModal;
</script>

<style>
@media only screen and (min-width: 768px) and (min-height: 600px) {
  .legal-confirmation-modal {
    --min-height: 90%;
    --min-width: min(900px, 90vw);
  }
}
</style>

<style scoped>
  ion-title {
    font-size: clamp(.8em, 4vw, 20px);
    font-weight: 500;
    letter-spacing: 0.0125em;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
  }

  .modal-confirmation-buttons {
    --button-spacing: 16px;
  }

  ion-content > * {
    margin: 20px;
  }

  hr {
    background: var(--ion-border-color);
    height: 1px;
    border-radius: 2px;
  }

  .confirm-button {
    --custom-size: 32px!important;
    margin-block: 0px;
    margin-inline: 2px;
  }

  .confirm-button ion-icon {
    margin-inline-start: 1px;
    margin-inline-end: 1px;
  }
</style>